import { useState } from 'react';
import { type ScoringItem } from '../types';
import { Plus, Trash2, ArrowRight, Upload } from 'lucide-react';
import { ExcelImportModal } from '../../../components/prioritization/import-export';
import type { AppField } from '../../../components/prioritization/import-export/ColumnMapper';

interface ItemsStepProps {
  items: ScoringItem[];
  onNext: (items: ScoringItem[]) => void;
}

// Field definitions for Excel import
const importFields: AppField[] = [
  { key: 'name', label: 'Name', required: true, type: 'string' },
  { key: 'description', label: 'Description', required: false, type: 'string' },
];

export function ItemsStep({ items, onNext }: ItemsStepProps) {
  const [itemNames, setItemNames] = useState<string[]>(
    items.length > 0 ? items.map((i) => i.name) : ['']
  );
  const [showImportModal, setShowImportModal] = useState(false);

  const handleAddItem = () => {
    setItemNames([...itemNames, '']);
  };

  const handleRemoveItem = (index: number) => {
    setItemNames(itemNames.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, value: string) => {
    const newItems = [...itemNames];
    newItems[index] = value;
    setItemNames(newItems);
  };

  const handleExcelImport = (data: Record<string, unknown>[]) => {
    // Extract names from imported data
    const importedNames = data
      .map(row => String(row.name || '').trim())
      .filter(name => name.length > 0);
    
    // Combine with existing items, removing empty entries
    const existingNames = itemNames.filter(name => name.trim().length > 0);
    const combined = [...existingNames, ...importedNames];
    
    // Remove duplicates while preserving order
    const unique = combined.filter(
      (name, index, self) => 
        index === self.findIndex(n => n.toLowerCase() === name.toLowerCase())
    );
    
    setItemNames(unique.length > 0 ? unique : ['']);
    setShowImportModal(false);
  };

  const handleNext = () => {
    const cleanedItems = itemNames
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name, index) => ({
        id: `item-${index}`,
        name,
      }));

    // Remove duplicates while preserving order
    const uniqueItems = cleanedItems.filter(
      (item, index, self) => index === self.findIndex((i) => i.name.toLowerCase() === item.name.toLowerCase())
    );

    if (uniqueItems.length < 2) {
      alert('Please add at least 2 items to compare.');
      return;
    }

    if (uniqueItems.length > 30) {
      const proceed = confirm(
        `You have ${uniqueItems.length} items. This may become unwieldy. Do you want to continue?`
      );
      if (!proceed) return;
    }

    onNext(uniqueItems);
  };

  const validItems = itemNames.filter((name) => name.trim().length > 0).length;

  return (
    <>
      {/* Excel Import Modal */}
      <ExcelImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleExcelImport}
        appFields={importFields}
        title="Import Items from Excel"
        description="Import a list of items to prioritize from an Excel file. Each row should have a name column."
      />

      <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
          Add Items to Prioritize
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-6">
          List the items you want to evaluate. Each item will be scored against your criteria.
        </p>

        <div className="space-y-2 mb-4">
          {itemNames.map((name, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => handleItemChange(index, e.target.value)}
                placeholder={`Item ${index + 1}`}
                className="flex-1 px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim()) {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
              />
              {itemNames.length > 1 && (
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="px-3 py-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleAddItem}
            className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] transition-colors"
          >
            <Upload className="h-4 w-4" />
            Import from Excel
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--rule)]">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--text-sec)]">
              {validItems} item{validItems !== 1 ? 's' : ''} added
            </p>
            <button
              onClick={handleNext}
              disabled={validItems < 2}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

